/**
 * Cluster 4 — Screen K : Variance Waterfall.
 *
 * Layout:
 *   - Header + month selector
 *   - Headline KPI strip (Total variance / Actual / Budget)
 *   - 5-leg variance waterfall (Pool price / Volume / Availability / Curtailment / Causer-pays)
 *   - Auto-generated narrative
 *   - Ancillary inputs panel (curtailment MWh, availability shortfall, causer-pays)
 *
 * Data: GET /api/portfolios/<id>/variance?month=YYYY-MM
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, Chip, TextField,
  MenuItem, Select, FormControl, InputLabel, Button,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import axios from 'axios';

import { fmtAUDCompact, fmtMWhCompact, fmtAUDPerMWh } from './KpiCard';
import KpiCard from './KpiCard';
import CommercialWorkspaceTabs from './CommercialWorkspaceTabs';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const LEG_COLOR = {
  'Pool price':         '#D98A2B',   // teal — capture price effect
  'Volume (residual)':  '#E9B065',   // purple — residual volume effect
  'Availability':       '#ef4444',   // red — outage/derating
  'Curtailment':        '#f59e0b',   // amber — network/economic
  'Causer-pays':        '#3b82f6',   // blue
};


function fmtAUDSigned(v) {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '−';
  return `${sign}${fmtAUDCompact(Math.abs(v))}`;
}

function lastNMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}


// ── Waterfall leg row ────────────────────────────────────────────────────

function LegRow({ leg, total, colors }) {
  const color = LEG_COLOR[leg.label] || colors.textMute;
  const isPos = leg.aud >= 0;
  const denom = Math.abs(total) > 0 ? Math.abs(total) : 1;
  const widthPct = Math.min(Math.abs(leg.aud) / denom * 100, 100);
  return (
    <Box sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 9, color, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', color: 'text.primary' }}>
            {leg.label}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexShrink: 0 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: '0.95rem',
            color: isPos ? '#22c55e' : '#ef4444',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {fmtAUDSigned(leg.aud)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary',
                                              minWidth: 60, textAlign: 'right',
                                              fontVariantNumeric: 'tabular-nums' }}>
            {leg.pct_of_variance >= 0 ? '+' : ''}{leg.pct_of_variance.toFixed(1)}%
          </Typography>
        </Stack>
      </Stack>
      {leg.note && (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 2.25, mb: 0.5 }}>
          {leg.note}
        </Typography>
      )}
      {/* Horizontal bar — split direction by sign */}
      <Box sx={{
        position: 'relative', height: 6, borderRadius: 3, bgcolor: colors.track, overflow: 'hidden', ml: 2.25,
      }}>
        <Box sx={{
          position: 'absolute', top: 0, bottom: 0,
          left: isPos ? '50%' : `${50 - widthPct / 2}%`,
          width: `${widthPct / 2}%`,
          bgcolor: color, opacity: 0.85, transition: 'all 240ms ease',
        }} />
        {/* Center divider */}
        <Box sx={{
          position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px',
          bgcolor: colors.hairline2,
        }} />
      </Box>
    </Box>
  );
}


// ── Page ─────────────────────────────────────────────────────────────────

export default function VarianceWaterfall() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState('');   // empty = backend picks latest

  const monthOptions = useMemo(() => lastNMonths(36), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = month ? { month } : {};
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/variance`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, month]);

  const attribution = data?.attribution;
  const total = attribution?.total_variance_aud || 0;
  const unavailable = new Set(data?.unavailable_inputs || []);

  if (data && data.available === false) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <CommercialWorkspaceTabs />
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', mb: 3 }}>
          Variance Waterfall
        </Typography>
        <Card sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1 }}>Not available</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto', mb: 3 }}>
            {data.reason || 'No active financial model for this portfolio.'}{' '}
            Budget-vs-actual variance is driven by the lender financial model —
            no demo or estimated figures are shown.
          </Typography>
          <Button variant="contained"
                  onClick={() => navigate(`/portfolios/${portfolioId}/contracts`)}>
            {data.cta || 'Upload & activate a financial model'}
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <CommercialWorkspaceTabs />
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
          Variance Waterfall
        </Typography>
        {data?.month_label && (
          <Chip label={data.month_label} size="small"
                sx={{ bgcolor: 'primary.main', color: 'background.paper',
                      fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Month</InputLabel>
          <Select
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            <MenuItem value=""><em>Latest</em></MenuItem>
            {monthOptions.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Headline KPI strip */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard
          label="Total variance" loading={loading}
          value={total != null ? fmtAUDSigned(total) : '—'}
          accent={total >= 0 ? '#22c55e' : '#ef4444'}
          tooltip="Actual revenue minus budget revenue for the selected month."
        />
        <KpiCard
          label="Actual revenue" loading={loading}
          value={fmtAUDCompact(data?.actual?.revenue_aud || 0)}
          unit={data?.actual?.volume_mwh != null ? `· ${fmtMWhCompact(data.actual.volume_mwh)}` : ''}
          tooltip={`Captured ${fmtAUDPerMWh(data?.actual?.avg_price || 0)}/MWh weighted average.`}
        />
        <KpiCard
          label="Budget revenue" loading={loading}
          value={fmtAUDCompact(data?.budget?.revenue_aud || 0)}
          unit={data?.budget?.volume_mwh != null ? `· ${fmtMWhCompact(data.budget.volume_mwh)}` : ''}
          tooltip={`Budget at ${fmtAUDPerMWh(data?.budget?.avg_price || 0)}/MWh.`}
        />
      </Box>

      {/* Narrative */}
      {!loading && data?.narrative && (
        <Card sx={{
          p: 2.25, mb: 2, borderLeft: '3px solid',
          borderColor: total >= 0 ? '#22c55e' : '#ef4444',
        }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Narrative
          </Typography>
          <Typography sx={{ color: 'text.primary', lineHeight: 1.5 }}>
            {data.narrative}
          </Typography>
        </Card>
      )}

      {/* Variance attribution waterfall */}
      <Card sx={{ p: 0, mb: 2 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between"
               sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            5-way attribution
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Pool price / Volume / Availability / Curtailment / Causer-pays
          </Typography>
        </Stack>
        <Box sx={{ px: 2.25, py: 1 }}>
          {loading ? (
            [1,2,3,4,5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={36} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
            ))
          ) : !attribution?.legs?.length ? (
            <Typography color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
              No variance data.
            </Typography>
          ) : (
            <>
              {attribution.legs.map((leg) => (
                <LegRow key={leg.label} leg={leg} total={total} colors={colors} />
              ))}
              {Math.abs(attribution.unattributed_aud) > 1 && (
                <Box sx={{ pt: 1.5, mt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                      Unattributed (cross-term + rounding)
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtAUDSigned(attribution.unattributed_aud)}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
      </Card>

      {/* Ancillary inputs */}
      <Card sx={{ p: 2.25 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Inputs to the attribution
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            From atlas + allocation engine
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={50} sx={{ bgcolor: colors.skeleton }} />
        ) : (
          <Box sx={{
            display: 'grid', gap: 2,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Curtailment loss
              </Typography>
              <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {fmtMWhCompact(data?.ancillary_inputs?.curtailment_loss_mwh || 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Network + neg-price (atlas split)
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Availability loss
              </Typography>
              {unavailable.has('availability') ||
               data?.ancillary_inputs?.availability_loss_mwh == null ? (
                <Typography sx={{ fontWeight: 600, fontStyle: 'italic', color: 'text.disabled' }}>
                  n/a
                </Typography>
              ) : (
                <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMWhCompact(data.ancillary_inputs.availability_loss_mwh)}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {unavailable.has('availability')
                  ? 'No real availability/expected-gen basis'
                  : '(1 - avail) × model expected generation'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Causer-pays
              </Typography>
              <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontStyle: 'italic',
                                color: 'text.disabled' }}>
                Pending
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                NETWORK_CAUSER_PAYS_SCADA — wires up next
              </Typography>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
}
